// frontend/src/components/UserProfile.js
// مكون عرض الملف الشخصي لمستخدم آخر
import React, { useState, useEffect } from 'react';
import { User, X, Trophy, Star, Users, MessageSquare, Shield, Eye, Gift, Copy, UserPlus, Check } from 'lucide-react';
import './UserProfile.css';
import { VipName } from './VipName';

const UserProfile = ({ userId, currentUser, onClose, onAnonymousMessage, vipSettings }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [friendRequestLoading, setFriendRequestLoading] = useState(false);
  const currentUserId = currentUser?.id || currentUser?.telegramId;

  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) return;
      try {
        const viewerParam = currentUserId ? `?viewerId=${currentUserId}` : '';
        const res = await fetch(`/api/users/${userId}/public-profile${viewerParam}`);
        if (res.ok) {
          const data = await res.json();
          setProfile(data.profile);
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId, currentUserId]);

  const handleAddFriend = async () => {
    if (!currentUserId || !profile?.telegramId) return;
    setFriendRequestLoading(true);
    try {
      const res = await fetch(`/api/friends/${currentUserId}/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId: profile.telegramId })
      });
      if (res.ok) {
        setProfile(prev => ({ ...prev, friendshipStatus: 'pending_sent' }));
        alert('تم إرسال طلب الصداقة بنجاح.');
      } else {
        const data = await res.json();
        alert(data.error || 'فشل إرسال طلب الصداقة.');
      }
    } catch (err) {
      console.error('Error sending friend request:', err);
      alert('حدث خطأ أثناء إرسال طلب الصداقة.');
    } finally {
      setFriendRequestLoading(false);
    }
  };

  const handleGiftVip = async () => {
      setPaymentLoading(true);
      try {
          const response = await fetch('/api/payment/stars/invoice', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ userId: profile.telegramId || profile.id })
          });

          const data = await response.json();
          if (data.ok && data.invoiceLink) {
              const tg = window.Telegram?.WebApp;
              if (tg) {
                  tg.openInvoice(data.invoiceLink, (status) => {
                      if (status === 'paid') {
                          alert(`🎁 تم إهداء وتفعيل اشتراك VIP الشهري لصديقك (${profile.nickname}) بنجاح! 🌟`);
                          setProfile(prev => ({ ...prev, isVip: true }));
                      } else if (status === 'failed') {
                          alert('❌ فشلت عملية الدفع بالنجوم.');
                      } else {
                          alert('⚠️ تم إلغاء عملية الدفع.');
                      }
                  });
              } else {
                  alert(`يرجى فتح البوت داخل تيليجرام لإتمام الدفع بالنجوم لاهداء صديقك.\nرابط الفاتورة: ${data.invoiceLink}`);
              }
          } else {
              alert('فشل توليد فاتورة الدفع: ' + (data.error || 'خطأ غير معروف'));
          }
      } catch (err) {
          console.error(err);
          alert('حدث خطأ في الاتصال بالخادم.');
      } finally {
          setPaymentLoading(false);
      }
  };

  const getRatingInfo = (rating) => {
    switch (rating) {
      case 'good': return { text: 'تقييم جيد', icon: '✅', cls: 'good' };
      case 'warn': return { text: 'تحت المراقبة', icon: '⚠️', cls: 'warn' };
      case 'bad': return { text: 'تقييم سيئ', icon: '❌', cls: 'bad' };
      default: return { text: 'تقييم جيد', icon: '✅', cls: 'good' };
    }
  };

  const handleCopyUsername = (username) => {
    if (!username) return;
    navigator.clipboard.writeText(`@${username}`);
    alert(`تم نسخ المعرف @${username} بنجاح!`);
  };

  return (
    <div className="user-profile-overlay" onClick={onClose}>
      <div className="user-profile-modal" onClick={(e) => e.stopPropagation()}>
        <button className="user-profile-close" onClick={onClose} aria-label="إغلاق" title="إغلاق">
          <X size={18} />
        </button>

        {loading ? (
          <div className="user-profile-loading">
            <div className="loader-spinner"></div>
          </div>
        ) : profile ? (
          <>
            <div className="user-profile-header">
              <div className="user-profile-avatar-large">
                {profile.avatarUrl ? (
                  <img src={profile.avatarUrl} alt="avatar" />
                ) : (
                  <User size={36} />
                )}
              </div>
              <h2 className="user-profile-name">
                <VipName user={{
                  nickname: profile.nickname,
                  isVip: profile.isVip,
                  customNameColor: profile.customNameColor
                }} />
              </h2>
              {profile.customUsername && (
                profile.isVip ? (
                  <div className="vip-username-frame-container">
                    <div className="vip-username-frame" onClick={() => handleCopyUsername(profile.customUsername)}>
                      <span className="crystal-icon">✦</span>
                      <span className="vip-username-text">@{profile.customUsername}</span>
                      <span className="crystal-icon">✦</span>
                      <button type="button" className="copy-username-btn" aria-label="نسخ المعرف">
                        <Copy size={13} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="user-profile-username">@{profile.customUsername}</p>
                )
              )}
              {profile.bio && (
                <p className="user-profile-bio">{profile.bio}</p>
              )}
              <div className="user-profile-level">
                <div className="user-profile-level-badge">
                  <Trophy size={14} />
                  مستوى {profile.level}
                </div>
                <div className="user-profile-level-badge" style={{ borderColor: 'rgba(0,136,204,0.2)', color: '#0088CC', background: 'rgba(0,136,204,0.08)' }}>
                  <Star size={14} />
                  {profile.xp} XP
                </div>
                {profile.age && profile.gender && (
                  <div className="user-profile-level-badge gender-age-badge">
                    <span>{profile.age} {profile.gender === 'ذكر' ? '♂' : '♀'}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="user-profile-stats">
              <div className="user-profile-stat">
                <span className="user-profile-stat-value">{profile.friendsCount || 0}</span>
                <span className="user-profile-stat-label">أصدقاء</span>
              </div>

              <div className="user-profile-stat">
                <span className="user-profile-stat-value">{profile.level || 1}</span>
                <span className="user-profile-stat-label">المستوى</span>
              </div>
            </div>

            {(() => {
              const ratingInfo = getRatingInfo(profile.accountRating);
              return (
                <div className={`user-profile-rating ${ratingInfo.cls}`}>
                  <Shield size={14} />
                  {ratingInfo.icon} {ratingInfo.text}
                </div>
              );
            })()}

            <div className="user-profile-actions">
              {profile.friendshipStatus === 'friends' && (
                <div className="friend-vip-active-badge">
                  <Check size={18} style={{ color: '#22c55e' }} />
                  <span>صديق مضاف لديك</span>
                </div>
              )}

              {profile.friendshipStatus === 'pending_sent' && (
                <div className="friend-vip-active-badge">
                  <UserPlus size={18} style={{ color: '#38bdf8' }} />
                  <span>طلب الصداقة مرسل</span>
                </div>
              )}

              {profile.friendshipStatus === 'none' && (
                <button
                  type="button"
                  className="btn-add-profile-friend"
                  onClick={handleAddFriend}
                  disabled={friendRequestLoading}
                >
                  <UserPlus size={18} />
                  {friendRequestLoading ? 'جاري الإرسال...' : 'إضافة صديق'}
                </button>
              )}

              {profile.isVip ? (
                <div className="friend-vip-active-badge">
                  <Star size={18} className="gold-star-spin" style={{ color: '#eab308' }} />
                  <span>صديقك لديه اشتراك VIP نشط</span>
                </div>
              ) : (
                <button 
                  type="button" 
                  className="btn-gift-vip" 
                  onClick={handleGiftVip}
                  disabled={paymentLoading}
                >
                  <Gift size={18} />
                  {paymentLoading ? 'جاري الاتصال...' : `أهدِ صديقك VIP (${vipSettings?.vipStarPrice || 50} نجمة)`}
                </button>
              )}

              {onAnonymousMessage && (
                <button className="btn-anon-message" onClick={() => onAnonymousMessage(userId)}>
                  <Eye size={18} />
                  أرسل رسالة مجهولة
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="user-profile-loading" style={{ padding: '40px' }}>
            <p style={{ color: '#999' }}>لم يتم العثور على المستخدم</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
